import React from 'react';
import { useAuth } from '../users/AuthContext';
import { Card, CardContent, Typography, Chip, Box, Avatar, Grid } from '@mui/material';
import { Person, Email, Group, Security, Business } from '@mui/icons-material';

const UserProfile = () => {
    const { 
        user, 
        getUserFullName, 
        getUserEmail, 
        getUserType, 
        getUserRoles, 
        getUserGroups,
        isAdmin,
        isStaff,
        isStudent,
        isApplicant
    } = useAuth();

    if (!user) {
        return (
            <Card>
                <CardContent>
                    <Typography color="textSecondary">
                        No user information available.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const getAvatarColor = () => {
        if (isAdmin()) return '#f44336'; // Red for admin
        if (isStaff()) return '#2196f3'; // Blue for staff
        if (isStudent()) return '#4caf50'; // Green for student
        if (isApplicant()) return '#ff9800'; // Orange for applicant
        return '#9e9e9e'; // Grey for others
    };

    const getRoleChipColor = (role) => {
        switch (role.toLowerCase()) {
            case 'admin': return 'error';
            case 'staff': return 'primary';
            case 'student': return 'success';
            case 'applicant': return 'warning';
            default: return 'default';
        }
    };

    return (
        <Card elevation={3} sx={{ maxWidth: 600, margin: 'auto', mt: 3 }}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={3}>
                    <Avatar 
                        sx={{ 
                            width: 80, 
                            height: 80, 
                            bgcolor: getAvatarColor(),
                            mr: 3,
                            fontSize: '2rem'
                        }}
                    >
                        {getUserFullName().charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                            {getUserFullName()}
                        </Typography>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            {getUserType()}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Person sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1">
                                <strong>Username:</strong> {user.username || 'N/A'}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Email sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1">
                                <strong>Email:</strong> {getUserEmail()}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Security sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1" sx={{ mr: 2 }}>
                                <strong>Roles:</strong>
                            </Typography>
                            {getUserRoles().length > 0 ? (
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {getUserRoles().map((role, index) => (
                                        <Chip
                                            key={index}
                                            label={role}
                                            color={getRoleChipColor(role)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No roles assigned
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Group sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1" sx={{ mr: 2 }}>
                                <strong>Groups:</strong>
                            </Typography>
                            {getUserGroups().length > 0 ? (
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {getUserGroups().map((group, index) => (
                                        <Chip
                                            key={index}
                                            label={group}
                                            color="info"
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No groups assigned
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    {/* Permission Summary */}
                    <Grid item xs={12}>
                        <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
                            <Typography variant="h6" gutterBottom>
                                <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Permissions Summary
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2">
                                        <strong>Admin Access:</strong> {isAdmin() ? '✅ Yes' : '❌ No'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2">
                                        <strong>Staff Access:</strong> {isStaff() ? '✅ Yes' : '❌ No'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2">
                                        <strong>Student Access:</strong> {isStudent() ? '✅ Yes' : '❌ No'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2">
                                        <strong>Applicant Access:</strong> {isApplicant() ? '✅ Yes' : '❌ No'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default UserProfile;
